/**
 * The rater's answer, as the state it replaces.
 *
 * One translation for both callers: the live loop commits it, and the draft
 * shows it over staged changes. A lower aggregate brings every limit above it
 * down to fit, and only the rater knows which.
 */
import { formatCoveragesForState } from '../../../transformers/coverages';
import { formatRatingOptions } from '../../../transformers/uw/ratingOptions';
import { formatComponentCoveragesForState } from '../../../transformers/componentCoverages';
import { formatComponentModifiersForState } from '../../../transformers/uw/componentRateModifiers';
import { formatComponentGlobals } from '../../../transformers/componentGlobals';
import {
  mergeCoverageReferralReasonsForEdit,
  mergeRateModifierReferralReasonsForEdit,
  mergeComponentReferralReasonsForEdit,
} from '../../../transformers/quote/mergeQuoteReferralReasonsForEdit';
import type { RaterXPreset } from '../../../api/types/rater-x-preset';
import type { RaterXRecalculatedPayload } from '../reducer';
import type { UnderwriteQuoteState } from '../types';

export function toRecalculatedPayload(
  quoteState: UnderwriteQuoteState,
  data: RaterXPreset
): RaterXRecalculatedPayload {
  const formattedCoverages = {
    ...formatComponentCoveragesForState(data.components),
    ...formatCoveragesForState(data.coverageCategories),
  };
  const formattedRatingOptions = {
    ...formatComponentModifiersForState(data.components),
    ...formatRatingOptions(data.rateModifierCategories),
  };
  const effectiveDate = data.effectiveDate?.date;
  const endDate = data.endDate?.date;

  // Start with formatted coverages/rating options (updated data from recalculation)
  // but preserve referral reasons from original quote state to keep them sticky
  const coveragesWithStickyReasons = { ...formattedCoverages };
  const ratingOptionsWithStickyReasons = { ...formattedRatingOptions };

  // Preserve existing referral reasons from quoteState
  // Only copy referral reasons if the coverage/rating option exists in formatted data
  Object.keys(quoteState.coverageState).forEach((key) => {
    if (
      coveragesWithStickyReasons[key] &&
      quoteState.coverageState[key]?.referralReasonsForEdit
    ) {
      coveragesWithStickyReasons[key] = {
        ...coveragesWithStickyReasons[key],
        referralReasonsForEdit:
          quoteState.coverageState[key].referralReasonsForEdit,
      };
    }
  });

  Object.keys(quoteState.ratingOptions).forEach((key) => {
    if (
      ratingOptionsWithStickyReasons[key] &&
      quoteState.ratingOptions[key]?.referralReasonsForEdit
    ) {
      ratingOptionsWithStickyReasons[key] = {
        ...ratingOptionsWithStickyReasons[key],
        referralReasonsForEdit:
          quoteState.ratingOptions[key].referralReasonsForEdit,
      };
    }
  });

  // Merge referral reasons for edit from all sources
  // This will add any NEW referral reasons from the response while preserving existing ones
  let mergedCoverages = mergeCoverageReferralReasonsForEdit(
    data.coverageCategories,
    coveragesWithStickyReasons
  );
  let mergedRatingOptions = mergeRateModifierReferralReasonsForEdit(
    data.rateModifierCategories,
    ratingOptionsWithStickyReasons
  );

  // Handle component-based structures (ZSP products)
  if (data.components) {
    const componentMerged = mergeComponentReferralReasonsForEdit(
      data.components,
      mergedCoverages,
      mergedRatingOptions
    );
    mergedCoverages = componentMerged.coverageState;
    mergedRatingOptions = componentMerged.ratingOptions;
  }

  return {
    componentGlobals: formatComponentGlobals(data.components),
    coverages: mergedCoverages,
    ratingOptions: mergedRatingOptions,
    global: data.global,
    totalPremium: data.totalPremium,
    carrierPrefix: data.carrierPrefix,
    treatyId: data.treatyId,
    ...(endDate && { endDate }),
    ...(effectiveDate && { effectiveDate }),
  };
}
